import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Upload, ShieldCheck, Trash2, Plus, Loader2, BadgeCheck, AlertTriangle, ShieldX, ShieldQuestion } from 'lucide-react';
import { LICENSE_TYPE_OPTIONS, LICENSE_TYPE_LABELS } from '../../constants/compliance';

// iter-fssai Phase 3 — a verified license within this many days of expiry
// (or already expired) requires the "I have renewed" confirmation modal
// before a redundant Verifico call is allowed — mirrors the backend's
// NEAR_EXPIRY_WINDOW_DAYS in services/compliance/fssai_verification.py.
const NEAR_EXPIRY_DAYS = 10;
const isNearExpiry = (lic) => {
  if (!lic.is_verified || !lic.expiry_date) return false;
  const exp = new Date(lic.expiry_date);
  const daysLeft = Math.ceil((exp - new Date()) / 86400000);
  return daysLeft <= NEAR_EXPIRY_DAYS;
};

// iter-fssai — Verifico verification badge + expiry date, shown for
// FSSAI rows regardless of editable/read-only mode (verifying doesn't
// change type/number, so it's always available).
const VerificationBadge = ({ lic }) => {
  if (lic.is_verified) {
    const exp = lic.expiry_date ? new Date(lic.expiry_date) : null;
    const daysLeft = exp ? Math.ceil((exp - new Date()) / 86400000) : null;
    if (daysLeft !== null && daysLeft < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid="license-verify-badge-expired">
          <ShieldX className="h-3 w-3" /> Expired on {lic.expiry_date}
        </span>
      );
    }
    if (daysLeft !== null && daysLeft <= 30) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="license-verify-badge-expiring">
          <AlertTriangle className="h-3 w-3" /> Verified · Expires in {daysLeft}d ({lic.expiry_date})
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="license-verify-badge-verified">
        <BadgeCheck className="h-3 w-3" /> Verified{lic.expiry_date ? ` · Exp: ${lic.expiry_date}` : ''}
      </span>
    );
  }
  if (lic.verification_status === 'failed') {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5"
        title={lic.last_verification_error || 'Verification failed'}
        data-testid="license-verify-badge-failed"
      >
        <ShieldX className="h-3 w-3" /> Verification Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5" data-testid="license-verify-badge-unverified">
      <ShieldQuestion className="h-3 w-3" /> Not Verified
    </span>
  );
};

// iter-374 / iter-374b — Reusable "Store-wide Licenses & Certifications"
// card. Used as-is (always editable) on the vendor's own Store Profile,
// and reused read-only-by-default (with an external Edit toggle) on the
// admin Vendor Details > Business Info tab. Kept as a single source of
// truth for the UI so both surfaces always look/behave identically.
export const StoreWideLicensesCard = ({
  licenses,
  onAdd,
  onUpdate,
  onRemove,
  onSave,
  saving,
  uploadingIdx,
  onUploadImage,
  onVerify,
  verifyingIdx,
  enforceAttemptLimit = true,
  editable = true,
  title = 'Store-wide Licenses & Certifications',
  description = "Add a license once here (e.g. FSSAI) and it auto-applies to every listing that vendor creates or already has — unless removed from a specific listing there. Distinct from the per-listing values entered directly on each product's Compliance section.",
  footerNote,
  testIdPrefix = 'vendor-license',
}) => {
  // iter-fssai Phase 3 — matches the backend's MAX_VENDOR_VERIFICATION_ATTEMPTS
  // (services/compliance/fssai_verification.py). Was previously 5, causing the
  // lockout UI to never trigger at the real backend-enforced limit of 3.
  const MAX_ATTEMPTS = 3;
  const [renewalModalIdx, setRenewalModalIdx] = useState(null);
  const [renewalChecked, setRenewalChecked] = useState(false);

  const closeRenewalModal = () => {
    setRenewalModalIdx(null);
    setRenewalChecked(false);
  };

  const renderVerifyControl = (lic, idx) => {
    const attemptsExceeded = enforceAttemptLimit && !lic.is_verified && (lic.verification_attempts || 0) >= MAX_ATTEMPTS;
    if (attemptsExceeded) {
      return (
        <span className="text-xs font-medium text-red-600" data-testid={`${testIdPrefix}-verify-capped-${idx}`}>
          {MAX_ATTEMPTS}/{MAX_ATTEMPTS} attempts used — contact admin to verify
        </span>
      );
    }
    return (
      <Button
        type="button"
        variant="default"
        size="sm"
        className="h-8 text-xs font-semibold shadow-sm"
        disabled={verifyingIdx === idx || !lic.number}
        onClick={() => (isNearExpiry(lic) ? setRenewalModalIdx(idx) : onVerify(idx, false))}
        data-testid={`${testIdPrefix}-verify-btn-${idx}`}
      >
        {verifyingIdx === idx ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
        {lic.is_verified ? 'Re-verify' : 'Verify'}
      </Button>
    );
  };
  return (
    <Card data-testid={`${testIdPrefix}-card`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {licenses.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid={`no-${testIdPrefix}s`}>
            No store-wide licenses added yet{editable ? ' — add one below to auto-apply it to all listings.' : '.'}
          </p>
        ) : !editable ? (
          <div className="space-y-2" data-testid={`${testIdPrefix}-readonly-list`}>
            {licenses.map((lic, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 rounded-lg border p-3 flex-wrap"
                data-testid={`${testIdPrefix}-readonly-row-${idx}`}
              >
                <BadgeCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 w-40 flex-shrink-0">
                  {LICENSE_TYPE_LABELS[lic.type] || lic.type}
                </span>
                <span className="text-sm text-gray-900 font-mono">{lic.number}</span>
                {lic.type === 'FSSAI' && (
                  <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                    <VerificationBadge lic={lic} />
                    {onVerify && renderVerifyControl(lic, idx)}
                  </div>
                )}
                {lic.image_url && (
                  <img src={lic.image_url} alt="Certificate" className="w-8 h-8 rounded object-cover border ml-auto" />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid={`${testIdPrefix}-list`}>
            {licenses.map((lic, idx) => {
              const isOther = lic.type === 'Other';
              return (
                <div key={idx} className="rounded-lg border p-3 space-y-2" data-testid={`${testIdPrefix}-row-${idx}`}>
                  <div className="flex gap-2 flex-wrap items-center">
                    <Select
                      value={lic.type}
                      onValueChange={(v) => onUpdate(idx, { type: v, image_url: v === 'Other' ? lic.image_url : '' })}
                    >
                      <SelectTrigger className="w-44 flex-shrink-0" data-testid={`${testIdPrefix}-type-select-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LICENSE_TYPE_OPTIONS.map((t) => (
                          <SelectItem key={t} value={t}>{LICENSE_TYPE_LABELS[t] || t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={lic.number || ''}
                      onChange={(e) => onUpdate(idx, { number: e.target.value })}
                      placeholder={`${LICENSE_TYPE_LABELS[lic.type] || lic.type} number`}
                      className="flex-1 min-w-[160px]"
                      data-testid={`${testIdPrefix}-number-input-${idx}`}
                    />
                    {lic.type === 'FSSAI' && (
                      <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto order-3 sm:order-none">
                        <VerificationBadge lic={lic} />
                        {onVerify && renderVerifyControl(lic, idx)}
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemove(idx)}
                      data-testid={`${testIdPrefix}-remove-btn-${idx}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  {isOther && (
                    <div className="flex items-center gap-2">
                      {lic.image_url ? (
                        <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-cert-preview-${idx}`}>
                          <img src={lic.image_url} alt="Certificate" className="w-10 h-10 rounded-lg object-cover border" />
                          <button
                            type="button"
                            onClick={() => onUpdate(idx, { image_url: '' })}
                            className="text-xs text-red-500"
                            data-testid={`${testIdPrefix}-cert-remove-btn-${idx}`}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <label
                          className="text-xs text-indigo-600 font-medium flex items-center gap-1 cursor-pointer"
                          data-testid={`${testIdPrefix}-cert-upload-btn-${idx}`}
                        >
                          <Upload className="h-3.5 w-3.5" />
                          {uploadingIdx === idx ? 'Uploading...' : 'Upload Certificate Photo (optional)'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploadingIdx === idx}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) onUploadImage(idx, file);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {editable && (
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={onAdd}
              className="text-sm text-indigo-600 font-medium flex items-center gap-1"
              data-testid={`add-${testIdPrefix}-btn`}
            >
              <Plus className="h-4 w-4" /> Add License / Certificate
            </button>
            <Button
              type="button"
              size="sm"
              disabled={saving}
              onClick={onSave}
              data-testid={`save-${testIdPrefix}s-btn`}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Licenses'
              )}
            </Button>
          </div>
        )}
        {footerNote && <div className="pt-2 text-xs text-gray-500" data-testid={`${testIdPrefix}-footer-note`}>{footerNote}</div>}
      </CardContent>
      <Dialog open={renewalModalIdx !== null} onOpenChange={(open) => { if (!open) closeRenewalModal(); }}>
        <DialogContent data-testid={`${testIdPrefix}-renewal-modal`}>
          <DialogHeader>
            <DialogTitle>Confirm License Renewal</DialogTitle>
            <DialogDescription>
              This FSSAI license is nearing or past its expiry date. Re-verifying calls Verifico
              again to fetch the latest status and expiry — please confirm you've renewed it (or
              want to re-check) before proceeding.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <Checkbox
              id={`${testIdPrefix}-renewal-checkbox`}
              checked={renewalChecked}
              onCheckedChange={(v) => setRenewalChecked(!!v)}
              data-testid={`${testIdPrefix}-renewal-checkbox`}
            />
            <label htmlFor={`${testIdPrefix}-renewal-checkbox`} className="text-sm cursor-pointer">
              I have renewed this license
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeRenewalModal} data-testid={`${testIdPrefix}-renewal-cancel-btn`}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!renewalChecked}
              onClick={() => { const idx = renewalModalIdx; closeRenewalModal(); onVerify(idx, true); }}
              data-testid={`${testIdPrefix}-renewal-confirm-btn`}
            >
              Re-verify Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default StoreWideLicensesCard;
